import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { SendConsultationCallBackRequestCommand } from '../../shared/models/commandModels/SendConsultationCallBackRequestCommand';
import { CommonService } from '../../shared/services/common.service';
import { PCXConstants } from '../../sc-config/constant/PCXConstants';
import {
    CaseServiceDocumentInfo,
    CaseServiceInfo,
    InsuranceCase,
    ServiceProviderContact
} from '../models/insurance-case.model';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors } from '@angular/forms';

export interface ContactWithPreference extends ServiceProviderContact {
    CommunicationPreferences?: {
        Email?: boolean;
        Sms?: boolean;
        Both?: boolean;
    };
    CommunicationDisabled?: {
        Email?: boolean;
        Sms?: boolean;
        Both?: boolean;
    };
    IsSelected?: boolean;
    ServiceItemId?: string;
}

export interface CallBackRequestForm {
    Phone: string;
    Email: string;
    Name: string;
    OfferNumber: string;
    ClaimNumber: string;
    ReceiverRole?: string;
}

@Injectable({
    providedIn: 'root'
})
export class ServiceProviderContactService {
    private readonly serviceProviderPreferences = new BehaviorSubject<ContactWithPreference[]>([]);
    readonly serviceProviderPreferences$ = this.serviceProviderPreferences.asObservable();

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly translateService: TranslateService
    ) {}

    public readonly communicationPreferenceItems: { label: string; key: string; iconClass: string }[] = [
        {
            label: 'APP_CREATE_CASE.SEND_EMAIL',
            key: 'Email',
            iconClass: 'pi pi-envelope'
        },
        {
            label: 'APP_CREATE_CASE.SEND_SMS',
            key: 'Sms',
            iconClass: 'pi pi-send'
        },
        {
            label: 'APP_CREATE_CASE.SEND_BOTH',
            key: 'Both',
            iconClass: 'pi pi-comments'
        }
    ] as const;

    public createServiceProviderContactViewForm(
        serviceForm: FormGroup,
        isPreferenceSetRequired: boolean = false
    ): FormArray | null {
        if (!serviceForm.get('ServiceProviderInfo')) {
            return null;
        }

        const caseService = serviceForm.getRawValue() as CaseServiceInfo;

        const isPreferenceSet = (contact: any, preference: 'Email' | 'Sms' | 'Both'): boolean => {
            if (!isPreferenceSetRequired) {
                return false;
            }

            const mailExists = Boolean(contact?.Email);
            const phoneExists = Boolean(contact?.Phone);

            if (preference === 'Email') {
                return mailExists && !phoneExists;
            }
            if (preference === 'Sms') {
                return !mailExists && phoneExists;
            }
            if (preference === 'Both') {
                return mailExists && phoneExists;
            }

            return false;
        };

        const checkIfPreferenceExists = (contact: any, preference: 'Email' | 'Sms' | 'Both'): boolean => {
            const mailExists = Boolean(contact?.Email);
            const phoneExists = Boolean(contact?.Phone);
            if (mailExists && preference === 'Email') {
                return true;
            }
            if (phoneExists && preference === 'Sms') {
                return true;
            }
            if (mailExists && phoneExists && preference === 'Both') {
                return true;
            }

            return false;
        };

        const contactViewForm = (contact: any): FormGroup => {
            const form = this.formBuilder.group({
                Name: [contact?.Name],
                Email: [contact?.Email || ''],
                Phone: [contact?.Phone || ''],
                Role: [contact?.Role || null],
                CommunicationPreferences: this.formBuilder.group({
                    Email: [isPreferenceSet(contact, 'Email') || false],
                    Sms: [isPreferenceSet(contact, 'Sms') || false],
                    Both: [isPreferenceSet(contact, 'Both') || false]
                }),
                CommunicationDisabled: this.formBuilder.group({
                    Email: [!checkIfPreferenceExists(contact, 'Email')],
                    Sms: [!checkIfPreferenceExists(contact, 'Sms')],
                    Both: [!checkIfPreferenceExists(contact, 'Both')]
                }),
                IsSelected: [contact?.IsSelected || isPreferenceSetRequired || false],
                ServiceItemId: [caseService?.ServiceItem?.ServiceItemId || null]
            });

            return form;
        };

        let serviceProviderContacts = [];
        (serviceForm.get('ServiceProviderInfo')?.get('Contacts') as FormArray)?.controls.forEach(control => {
            const contactInfo = control.getRawValue() as ServiceProviderContact;

            // if name field is empty, then this contact will not show in the view
            if (!contactInfo.Name) {
                return;
            }

            // if email is invalid, set the email to empty
            if (control.get('Email').invalid) {
                contactInfo.Email = '';
            }

            // if phone is invalid, set the phone to empty
            if (control.get('Phone').invalid) {
                contactInfo.Phone = '';
            }

            // if email and phone are empty, skip the contact
            if (!contactInfo.Email && !contactInfo.Phone) {
                return;
            }

            // add the contact to the array
            serviceProviderContacts.push(contactInfo);
        });

        return this.formBuilder.array(serviceProviderContacts.map(contact => contactViewForm(contact)));
    }

    public convertContactPreferenceToCallBackRequest(
        contactWithPreferences: ContactWithPreference[],
        offerNumber: string,
        claimNumber: string
    ): CallBackRequestForm[] {
        // if no contacts selected, return empty array
        if (contactWithPreferences.length === 0) {
            return [];
        }

        let commands: any[] = [];
        contactWithPreferences.forEach(contact => {
            const isPhoneSelected = contact?.CommunicationPreferences?.Both || contact?.CommunicationPreferences.Sms;
            const isEmailSelected = contact?.CommunicationPreferences?.Both || contact?.CommunicationPreferences?.Email;

            const phoneNumber = isPhoneSelected ? contact.Phone : '';
            const emailAddress = isEmailSelected ? contact.Email : '';

            commands.push({
                Phone: phoneNumber,
                Email: emailAddress,
                Name: contact.Name || '',
                OfferNumber: offerNumber || '',
                ClaimNumber: claimNumber || '',
                ReceiverRole: contact.Role || ''
            } as CallBackRequestForm);
        });

        return commands;
    }

    public async convertCallbackRequestToSendRequestCommands(
        callbackRequests: CallBackRequestForm[],
        insuranceCase: InsuranceCase,
        caseServiceInfo: CaseServiceInfo
    ): Promise<SendConsultationCallBackRequestCommand[]> {
        const commands = await Promise.all(
            callbackRequests.map(request =>
                this.prepareCallBackRequestCommand(
                    request,
                    insuranceCase.ItemId,
                    insuranceCase.Language,
                    caseServiceInfo,
                    insuranceCase.CaseDocumentList
                )
            )
        );

        return commands;
    }

    public async prepareCallBackRequestCommand(
        formData: CallBackRequestForm,
        CaseId: string,
        Language: string,
        caseServiceInfo: CaseServiceInfo,
        caseDocumentList: CaseServiceDocumentInfo[],
        IsFromPolicyHolder: boolean = false
    ): Promise<SendConsultationCallBackRequestCommand> {
        const { Phone = '', Email = '', Name = '', OfferNumber = '', ClaimNumber = '' } = formData;
        const documentType = this.getDocumentType(caseServiceInfo, caseDocumentList);
        let documentTypeTranslation = documentType
            ? await this.getTranslation(Language, `APP_CASES.${documentType}`)
            : '';

        return {
            CaseId: CaseId,
            ServiceItemId: caseServiceInfo?.ServiceItem?.ServiceItemId ?? '',
            RequestedPhoneNumber: Phone,
            ReceiverEmail: Email,
            ReceiverName: Name,
            OfferNumber: OfferNumber,
            DocumentType:
                documentType === PCXConstants.CaseDocumentTypes.INVOICE_TYPE
                    ? documentTypeTranslation?.toLowerCase()
                    : documentTypeTranslation,
            Language: Language,
            IsFromPolicyHolder: IsFromPolicyHolder,
            ClaimNumber: ClaimNumber,
            ReceiverRole: formData.ReceiverRole || ''
        } as SendConsultationCallBackRequestCommand;
    }

    private getDocumentType(caseServiceInfo: any, caseDocumentList: any[]): string {
        const caseServiceFileId = caseServiceInfo?.CaseDocumentInfo?.FileId;
        if (!caseServiceFileId) {
            return '';
        }

        let documentType = caseDocumentList?.find(caseDoc => caseDoc.FileId === caseServiceFileId)?.DocumentType ?? '';
        return documentType;
    }

    private async getTranslation(caseLanguage: string, key: string): Promise<string> {
        if (!key) {
            return '';
        }

        const language = CommonService.getLangFromLanguageKey(caseLanguage); // en, de, fr, it it this type
        await firstValueFrom(this.translateService.getTranslation(language)); // fetching all the translation of current selected language
        const translation = this.translateService.getParsedResult(this.translateService.translations[language], key);
        return translation;
    }

    public phoneStartsWithValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value as string;
        if (!value) return null;

        let isSwiss = false;
        let localNumber = value.replace(/\s/g, '');

        if (localNumber.startsWith('+41')) {
            isSwiss = true;
            localNumber = localNumber.replace('+41', '');
        } else if (localNumber.startsWith('0041')) {
            isSwiss = true;
            localNumber = localNumber.replace('0041', '');
        }

        if (isSwiss) {
            if (!/^\d{9}$/.test(localNumber)) {
                return { validatePhoneNumber: true, landlineNumber: false };
            }
            if (localNumber.startsWith('0')) {
                return { validatePhoneNumber: false, landlineNumber: true };
            }
            if (!/^7[4-9]/.test(localNumber)) {
                return { validatePhoneNumber: false, landlineNumber: true };
            }

            return null;
        }

        return null;
    }

    getUniquePreference(contacts: ContactWithPreference[]): ContactWithPreference[] {
        const seen = new Set<string>();
        const uniqueContacts: ContactWithPreference[] = [];

        contacts.forEach(contact => {
            const key = `${contact.Email || ''}-${contact.Phone || ''}-${contact.ServiceItemId || ''}`;

            if (!seen.has(key)) {
                seen.add(key);
                uniqueContacts.push(contact);
            }
        });

        return uniqueContacts;
    }

    public updateServiceProviderPreferences(
        contactWithPreferences: ContactWithPreference[],
        serviceItemId: string
    ): void {
        let preferences = this.serviceProviderPreferences.getValue();

        // removing all preferences that were previously saved
        preferences = preferences.filter(item => item.ServiceItemId !== serviceItemId);

        // adding current preference
        preferences = preferences.concat(contactWithPreferences);

        // removing duplicates
        preferences = this.getUniquePreference(preferences);

        // updating state
        this.serviceProviderPreferences.next(preferences);
    }

    public getServiceProviderPreferences(): ContactWithPreference[] {
        return this.serviceProviderPreferences.getValue() || [];
    }

    public resetServiceProviderPreferences(): void {
        this.serviceProviderPreferences.next([]);
    }

    public updatePreferenceFormIfCallbackRequestExist(preferenceForm: FormArray): FormArray {
        const existingPreference = this.getServiceProviderPreferences();
        if (existingPreference.length === 0) {
            return preferenceForm;
        }

        preferenceForm.controls.forEach(control => {
            const contact = control.getRawValue() as ContactWithPreference;
            const preference = existingPreference.find(
                x =>
                    x.Name === contact.Name &&
                    x.Phone === contact.Phone &&
                    x.Email === contact.Email &&
                    x.ServiceItemId === contact.ServiceItemId
            );

            // either contact info is changed or not found
            if (!preference) {
                return;
            }

            // setting up the previous control value
            control.get('IsSelected').setValue(preference.IsSelected);
            control.get('CommunicationPreferences').get('Email')?.setValue(preference.CommunicationPreferences?.Email);
            control.get('CommunicationPreferences').get('Sms')?.setValue(preference.CommunicationPreferences?.Sms);
            control.get('CommunicationPreferences').get('Both')?.setValue(preference.CommunicationPreferences?.Both);
        });

        return preferenceForm;
    }
}
